import type {
  ChangePasswordInput,
  LoginInput,
  Preferences,
  RegisterInput,
  UpdateProfileInput,
  UserDto,
} from '@keep/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

export const meKey = ['me'] as const;

export interface AppConfig {
  allowRegistration: boolean;
  version: string;
}

export function useAppConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: () => api.get<AppConfig>('/config'),
    staleTime: Infinity,
  });
}

/** Utilisateur connecté, ou `null` si aucune session. */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      try {
        return await api.get<UserDto>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/** Raccourci pour les composants rendus sous une route authentifiée. */
export function useCurrentUser(): UserDto {
  const { data } = useMe();
  if (!data) throw new Error('useCurrentUser appelé hors session');
  return data;
}

export function usePreferences(): Preferences {
  return useCurrentUser().preferences;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<UserDto>('/auth/login', input),
    onSuccess: (user) => {
      queryClient.clear();
      queryClient.setQueryData(meKey, user);
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterInput) => api.post<UserDto>('/auth/register', input),
    onSuccess: (user) => {
      queryClient.clear();
      queryClient.setQueryData(meKey, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(meKey, null);
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => api.patch<UserDto>('/auth/me', input),
    // Les préférences (thème, disposition) s'appliquent immédiatement.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: meKey });
      const previous = queryClient.getQueryData<UserDto | null>(meKey);
      if (previous) {
        queryClient.setQueryData<UserDto>(meKey, {
          ...previous,
          ...(input.name && { name: input.name }),
          preferences: { ...previous.preferences, ...input.preferences },
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => queryClient.setQueryData(meKey, context?.previous),
    onSuccess: (user) => queryClient.setQueryData(meKey, user),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => api.post<void>('/auth/password', input),
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => api.delete('/auth/me', { password }),
    onSuccess: () => {
      queryClient.clear();
      queryClient.setQueryData(meKey, null);
    },
  });
}
